import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import * as CartKeys from 'tripPlanner/hooks/reactQueryKeys/cart'
import {
  isMissingTripPlannerCartError,
  mapTripPlannerCartItemsToMap,
  type TripPlannerCartItemsData,
} from '../utils/tripPlannerCartItemsUtils'

export const tripPlannerCartItemsKey = CartKeys.tripPlannerCartItems

interface UseTripPlannerCartItemsArgs {
  multiCartId: string | undefined
  isLoggedIn: boolean
  enabled?: boolean
}

interface UseTripPlannerCartItemsReturn {
  data: TripPlannerCartItemsData | undefined
  cartItemsMap: Map<string, App.MultiCartItem>
  isLoading: boolean
  isFetching: boolean
  isSuccess: boolean
  isError: boolean
  isMissingCart: boolean
  dataUpdatedAt: number
  errorUpdatedAt: number
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

function toRefetchError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Unable to refetch Trip Planner cart items')
}

export function useTripPlannerCartItems({
  multiCartId,
  isLoggedIn,
  enabled = true,
}: UseTripPlannerCartItemsArgs): UseTripPlannerCartItemsReturn {
  const queryEnabled = enabled && isLoggedIn && !!multiCartId
  const {
    data,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    dataUpdatedAt,
    errorUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: tripPlannerCartItemsKey(multiCartId),
    queryFn: () => {
      if (!multiCartId) throw new Error('multiCartId is required for Trip Planner cart items')
      return getMultiCartItems(multiCartId)
    },
    enabled: queryEnabled,
    refetchOnMount: true,
    retry: (failureCount, queryError) => !isMissingTripPlannerCartError(queryError) && failureCount < 1,
  })

  const effectiveData = queryEnabled ? data : undefined
  const cartItemsMap = useMemo(
    () => mapTripPlannerCartItemsToMap(effectiveData, multiCartId),
    [effectiveData, multiCartId],
  )

  const refetchCartItems = useCallback(async() => {
    if (!queryEnabled) return new Map<string, App.MultiCartItem>()
    const result = await refetch()
    if (result.isError) {
      throw toRefetchError(result.error)
    }
    return mapTripPlannerCartItemsToMap(result.data, multiCartId)
  }, [multiCartId, queryEnabled, refetch])

  return {
    data: effectiveData,
    cartItemsMap,
    isLoading: queryEnabled && isLoading,
    isFetching: queryEnabled && isFetching,
    isSuccess: queryEnabled && isSuccess,
    isError: queryEnabled && isError,
    isMissingCart: queryEnabled && isError && isMissingTripPlannerCartError(error),
    dataUpdatedAt: queryEnabled ? dataUpdatedAt : 0,
    errorUpdatedAt: queryEnabled ? errorUpdatedAt : 0,
    refetchCartItems,
  }
}
